import { createAuthenticatedApiClient } from "../apiClient";

const baseUrl = process.env.REACT_APP_NODEJS_SERVER_URL;

const api = createAuthenticatedApiClient({
  baseURL: `${baseUrl}/api`,
});

const withData = (response) => response?.data || {};

export const listActiveOrderFileImports = async ({ instanceUniqueId, orderId, customerId = null }) =>
  withData(await api.get(customerId
    ? `/instances/${instanceUniqueId}/customers/${customerId}/file-imports`
    : `/instances/${instanceUniqueId}/orders/${orderId}/file-imports`));

export const listActiveCustomerFileImports = async ({ instanceUniqueId, customerId }) =>
  withData(await api.get(`/instances/${instanceUniqueId}/customers/${customerId}/file-imports`));

export const createOrderFileImportSession = async ({
  instanceUniqueId,
  orderId,
  customerId = null,
  scopeType = "order",
  destinationFolderId = null,
  rootMode = "preserve",
  conflictMode = "keep_both",
  idempotencyKey = null,
}) => {
  const isCustomerScope = scopeType === "customer";
  const path = isCustomerScope
    ? `/instances/${instanceUniqueId}/customers/${customerId}/file-imports`
    : `/instances/${instanceUniqueId}/orders/${orderId}/file-imports`;

  return withData(await api.post(path, {
    customerId,
    destinationFolderId,
    rootMode,
    conflictMode,
    idempotencyKey,
    scopeType,
  }));
};

export const getOrderFileImportSession = async (sessionId) =>
  withData(await api.get(`/file-imports/${sessionId}`));

export const getOrderFileImportItems = async (sessionId, params = {}) =>
  withData(await api.get(`/file-imports/${sessionId}/items`, { params }));

export const submitOrderFileImportManifestBatch = async (sessionId, items) =>
  withData(await api.post(`/file-imports/${sessionId}/manifest-batches`, { items }));

export const prepareOrderFileImportSession = async (sessionId) =>
  withData(await api.post(`/file-imports/${sessionId}/prepare`));

export const pauseOrderFileImportSession = async (sessionId) =>
  withData(await api.post(`/file-imports/${sessionId}/pause`));

export const resumeOrderFileImportSession = async (sessionId) =>
  withData(await api.post(`/file-imports/${sessionId}/resume`));

export const cancelOrderFileImportSession = async (sessionId) =>
  withData(await api.post(`/file-imports/${sessionId}/cancel`));

export const finalizeOrderFileImportSession = async (sessionId) =>
  withData(await api.post(`/file-imports/${sessionId}/finalize`));

export const retryFailedOrderFileImportItems = async (sessionId, itemIds = null) =>
  withData(await api.post(`/file-imports/${sessionId}/retry-failed`, { itemIds }));

export const initiateOrderFileImportItemUpload = async (sessionId, itemId) =>
  withData(await api.post(`/file-imports/${sessionId}/items/${itemId}/upload-initiate`));

export const uploadOrderFileImportItem = async (sessionId, itemId, file, config = {}) => {
  const formData = new FormData();
  formData.append("file", file);
  return withData(await api.post(
    `/file-imports/${sessionId}/items/${itemId}/upload`,
    formData,
    config
  ));
};

export const getOrderFileImportMultipartPartUrl = async (sessionId, itemId, partNumber) =>
  withData(await api.post(`/file-imports/${sessionId}/items/${itemId}/multipart-part-url`, { partNumber }));

export const completeOrderFileImportSingleUpload = async (sessionId, itemId) =>
  withData(await api.post(`/file-imports/${sessionId}/items/${itemId}/complete-single`));

export const completeOrderFileImportMultipartUpload = async (sessionId, itemId, parts) =>
  withData(await api.post(`/file-imports/${sessionId}/items/${itemId}/complete-multipart`, { parts }));

export const failOrderFileImportItemUpload = async (
  sessionId,
  itemId,
  { errorCode, errorMessage, failurePhase } = {}
) =>
  withData(
    await api.post(`/file-imports/${sessionId}/items/${itemId}/fail`, {
      errorCode,
      errorMessage,
      failurePhase,
    })
  );
